import { Component, OnInit, OnDestroy, ViewEncapsulation, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { of, Subject, forkJoin } from 'rxjs';
import { map, takeUntil, catchError } from 'rxjs/operators';
import { FilterService } from 'primeng/api';
import { Table, TableFilterEvent } from 'primeng/table';
import { Title } from "@angular/platform-browser";
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { DocListEntry } from 'src/app/shared/doc-list-entry';
import { RestApiService } from 'src/app/services/rest-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';


interface OrderListFilterState {
  DOCNUMBER?:      string;
  DOCDATE?:        string;
  LOCALCOMP?:      string;
  AUTHOR?:         string;
  PARTNERNAMESTR?: string;
  DOCSUM?:         string;
  PAYDSUM?:        string;
  SHIPSUM?:        string;
  SHIPPED?:        string;
  READY2SHIP?:     string;
  DOCNOTE1?:       string;
  DOCNOTE2?:       string;
}

interface FilterMetadata {
  value: any;
  matchMode?: string;
}

interface DocumentWithShipment extends DocListEntry {

  READY2SHIP?: number;
  SHIPPED?: number;

  DEBUG?: string;
};

/**
 * Dashboard widget showing paid-but-not-fully-shipped orders with real-time stock readiness check.
 *
 * Data-flow phase 1: `loadNotShippedOrdersList()` → `GET /dashboard/notshippedpaidorders` → list rendered.
 * Data-flow phase 2 (auto-triggered): `_checkShipmentReadiness()` → parallel `forkJoin` per document
 *   → `getDocItemsByDoc` + `getDocItemsStockAndShipped` → `READY2SHIP` column populated.
 *
 * Key collaborators: `RestApiService` (order list), `DocumentApiService` (items + stock),
 * `FilterService` (custom `gtOrZero` / `GteOrZero` PrimeNG table filters).
 * Stock depot IDs: `ms` (main), `is` (import, conditionally used), `as` (aux, conditionally used).
 */
@Component({
  selector: 'app-home-not-shipped-orders-list',
  templateUrl: './home-not-shipped-orders-list.component.html',
  styleUrls: ['./home-not-shipped-orders-list.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class HomeNotShippedOrdersListComponent implements OnInit, OnDestroy, OnChanges {

  /** Reference date for order list depth calculation and stock/shipped queries. */
  @Input() today: Date;
  /** When set to `true`, clears the PrimeNG table saved state before reload. */
  @Input() clean: boolean = true;

  globalSettinsData: GlobalSettingsData;

  @ViewChild('ptable4') ptable4: Table;

  notShippedOrdersList: DocumentWithShipment[];
  notShippedSelectedDocument: DocumentWithShipment;
  totalNotShippedOrders: number;

  loading: boolean = false;

  totalInvoiceSum: number = 0;
  totalPaidSum: number = 0;
  totalShippedSum: number = 0;

  // to store f[string] filters value

  cf4: OrderListFilterState = {};

  private readonly destroy$ = new Subject<void>();
  private ms: number = 0;
  private is: number = 0;
  private as: number = 0;


  constructor(
    private restapi: RestApiService,
    private docApi: DocumentApiService,
    private filterService: FilterService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,

  ) { }

  /** Fetches paid-not-shipped orders; computes `SHIPPED %` and totals; auto-calls `_checkShipmentReadiness`. */
  loadNotShippedOrdersList(): void {
    this.loading = true;
    this.restapi.getNotShippedOrdersList(
      this.globalSettinsData.mainCompanyId, 365, this.today
    ).pipe(
      map(data => {
        this.notShippedOrdersList = data || [];
        this.totalNotShippedOrders = this.notShippedOrdersList.length;
        this.docListCalculatedFields(this.notShippedOrdersList);
        this.totalInvoiceSum = this.notShippedOrdersList.reduce((s, e) => s + e.DOCSUM, 0);
        this.totalPaidSum    = this.notShippedOrdersList.reduce((s, e) => s + e.PAYDSUM, 0);
        this.totalShippedSum = this.notShippedOrdersList.reduce((s, e) => s + e.SHIPSUM, 0);
        this.notShippedOrdersList.forEach(e => {
          e.SHIPPED = Math.round(e.SHIPSUM / e.DOCSUM * 100);
        });
        this.loading = false;
        this._checkShipmentReadiness();
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      error: err => {
        console.error('*** HomeNotShippedOrdersListComponent loadNotShippedOrdersList error:', err);
        this.loading = false;
      }
    });
  }

  /** `clean` → clear table state then reload; `today` → reload list. */
  ngOnChanges(changes: SimpleChanges) {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent ngOnChanges:');
    for (const propName of Object.keys(changes)) {
      const change = changes[propName];

      switch (propName) {
        case 'clean':
          this.clearSavedSettings();
          this.loadNotShippedOrdersList();
          break;
        case 'today':
          if (this.globalSettinsData) {
            this.loadNotShippedOrdersList();
          }
          break;
      }
    }
  }

  /** Completes `destroy$` to cancel all in-flight subscriptions. */
  ngOnDestroy(): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Registers custom `FilterService` comparators; loads order list on init. */
  ngOnInit(): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent in ngOnInit');

    this.today = new Date();

    this.titleService.setTitle('Оплаченные и не отгруженные заказы - домашняя страница');
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.ms = this.globalSettinsData.mainStockId;
    this.is = this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0;
    this.as = this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0;

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter === 0) && (value === 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter === 0) && (value === 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.loadNotShippedOrdersList();
  }

  /** PrimeNG table state-save hook — debug log only. */
  onStateSave(state: unknown): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent onStateSave: ' + JSON.stringify(state));
  }

  /** PrimeNG table state-restore hook — repopulates `cf4` filter model. */
  onStateRestorePaidNotShipped(state: { filters?: Record<string, FilterMetadata | FilterMetadata[]> }): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent onStateRestorePaidNotShipped: ' + JSON.stringify(state));
    if (state.filters) {
      for (const f of Object.keys(state.filters)) {
        const fltItem = state.filters[f];
        this.cf4[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  /** Computes boolean display flags (`fs`, `ps`, `fp`, `pp`, `np`, `go`, `td`, `fd`) on each entry. */
  docListCalculatedFields(orderList: DocListEntry[]) {

    let jn: Date = new Date();
    let td: Date = new Date();
    td.setHours(0, 0, 0, 0);
    let tm: Date = new Date(td);
    tm.setHours(23, 59, 59);

    orderList.forEach((dc: DocListEntry) => {
      dc.fs = (dc.DOCTYPEID === 1) && (dc.DOCSUM > 0) && (Math.abs(dc.DOCSUM - dc.SHIPSUM) < 0.02);

      dc.ps = (dc.DOCTYPEID === 1) && (dc.DOCSUM > 0) && (dc.SHIPSUM > 0) && ((dc.DOCSUM - dc.SHIPSUM) > 0.02);

      dc.fp = (dc.DOCTYPEID === 1) && (dc.DOCSUM > 0) && (Math.abs(dc.DOCSUM - dc.PAYDSUM) < 0.02);

      dc.pp = (dc.DOCTYPEID === 1) && (dc.DOCSUM > 0) && (dc.PAYDSUM > 0) && (Math.abs(dc.DOCSUM - dc.PAYDSUM) > 0.02);

      dc.np = (dc.DOCTYPEID === 1) && (dc.DOCSUM > 0) && ((dc.SHIPSUM - dc.PAYDSUM) > 0.02);

      dc.go = (dc.DOCTYPEID === 1) && (new Date(dc.DOCVALDATE) < jn) && (dc.SHIPSUM === 0) && (dc.PAYDSUM === 0);

      dc.td = (new Date(dc.DOCDATE) > td) && (new Date(dc.DOCDATE) < tm);

      dc.fd = (new Date(dc.DOCDATE) > tm);
    });
  }

  /** Clears PrimeNG table local-storage state; resets `clean = false`. */
  clearSavedSettings() {
    try {
      this.ptable4.clearState();
    } catch (e) {
      environment.production || console.log('*** HomeNotShippedOrdersListComponent in clearSavedSettings: ' + JSON.stringify(e));
    }
    this.clean = false;
  }

  /** Toolbar reload button handler — calls `loadNotShippedOrdersList`. */
  reloadTable(_e: unknown): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent reloadTable');

    this.loadNotShippedOrdersList();
  }

  /** Toolbar reset-filter button handler — calls `clearSavedSettings`. */
  clearOrResetTableFilter(_e: unknown): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent clearOrResetTableFilter');

    this.clearSavedSettings();
  }

  /** Recomputes visible totals and `SHIPPED %` on PrimeNG filter change. */
  onFilterEvent(event: TableFilterEvent): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent onFilterEvent');

    this.totalInvoiceSum = event.filteredValue.reduce((s, e) => s += e.DOCSUM, 0);
    this.totalPaidSum = event.filteredValue.reduce((s, e) => s += e.PAYDSUM, 0);
    this.totalShippedSum = event.filteredValue.reduce((s, e) => s += e.SHIPSUM, 0);
    event.filteredValue.forEach(e => {

      e.SHIPPED = Math.round(e.SHIPSUM / e.DOCSUM * 100);

    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });

      saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
    });
  }

  /** Exports current `notShippedOrdersList` to XLSX via `xlsx` + `file-saver`. */
  onExportData(): void {
    environment.production || console.log('*** HomeNotShippedOrdersListComponent onExportData');

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.notShippedOrdersList);

      const exportData: any =
        this.notShippedOrdersList.map(e => ({

          'Документ': e.DOCNAME,
          'Номер': e.DOCNUMBER,
          'Дата': new Date(e.DOCDATE),
          'Покупатель': e.CLIENTSNAME,
          'Ответственный': e.PERSONNAME,
          'Продавец': e.LOCALCOMP,
          'Менеджер': e.AUTHOR,
          'Сумма': e.DOCSUM,
          'Оплачено': e.PAYDSUM,
          'Отгружено': e.SHIPSUM,
          'Валюта': e.CURSTR,
          'Курс': e.DOCRATEVAL,
          'Примечание продавца': e.DOCNOTE1,
          'Примечание покупателя': e.DOCNOTE2,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { documents: worksheet }, SheetNames: ['documents'] };

      const excelBuffer: any = xlsx.write(workbook, { bookType: "xlsx", type: "array" });

      this.saveAsExcelFile(excelBuffer, "documents");
    });
  }

  /** Manual refresh — re-runs `_checkShipmentReadiness` without reloading the order list. */
  checkShipmentPossibility(_e: unknown): void {
    this._checkShipmentReadiness();
  }

  /**
   * Parallel `forkJoin` across all loaded documents: `getDocItemsByDoc` + `getDocItemsStockAndShipped`
   * → computes `READY2SHIP %` per document. Complexity: O(N) parallel (2 calls per doc).
   */
  private _checkShipmentReadiness(): void {
    if (!this.notShippedOrdersList?.length) { return; }
    this.loading = true;
    const now = new Date();
    forkJoin(
      this.notShippedOrdersList.map(doc =>
        forkJoin({
          items:   this.docApi.getDocItemsByDoc(doc.DOCID),
          summary: this.docApi.getDocItemsStockAndShipped(
                     doc.DOCID, doc.DOCMANCLIID, this.ms, this.is, this.as, now)
        }).pipe(
          map(({ items, summary }) => {
            const summaryMap = new Map(summary.map(s => [s.DOCITEMID, s]));
            const need = items.reduce((sum, di) => {
              const s = summaryMap.get(di.DOCITEMID);
              return sum + Math.max(0, di.DOCITEMQTY - (s?.DOCITEMSHIPPED ?? 0));
            }, 0);
            const have = items.reduce((sum, di) => {
              const s = summaryMap.get(di.DOCITEMID);
              const remaining = Math.max(0, di.DOCITEMQTY - (s?.DOCITEMSHIPPED ?? 0));
              return sum + Math.min(remaining, s?.PRODSTOCK ?? 0);
            }, 0);
            doc.READY2SHIP = need > 0 ? Math.round(have / need * 100) : 100;
            environment.production || console.log(
              `*** HomeNotShippedOrdersListComponent READY2SHIP doc ${doc.DOCID}: need=${need}, have=${have}, result=${doc.READY2SHIP}`
            );
          }),
          catchError(err => {
            console.error(
              `*** HomeNotShippedOrdersListComponent _checkShipmentReadiness error doc ${doc.DOCID}:`, err
            );
            doc.READY2SHIP = null;
            return of(null);
          })
        )
      )
    ).pipe(
      map(() => { this.loading = false; }),
      takeUntil(this.destroy$)
    ).subscribe({
      error: err => {
        console.error('*** HomeNotShippedOrdersListComponent _checkShipmentReadiness error:', err);
        this.loading = false;
      }
    });
  }
}
